// File: client/src/components/rules/expressionEditor/MonacoLanguageUtils.js

/**
 * Check if the cursor is inside an EJS expression
 * @param {string} text - The text to check
 * @param {number} position - The cursor position
 * @returns {Object} Information about the EJS context
 */
export const isInsideEjsExpression = (text, position) => {
    if (!text) return { inside: false };

    const textBeforeCursor = text.substring(0, position);
    const textAfterCursor = text.substring(position);

    // Find last opening tags before cursor
    const lastOpenEqualsPos = textBeforeCursor.lastIndexOf('<%=');
    const lastOpenHyphenPos = textBeforeCursor.lastIndexOf('<%-');
    const lastOpenScriptletPos = textBeforeCursor.lastIndexOf('<%');

    // Find last closing tag before cursor
    const lastClosePos = textBeforeCursor.lastIndexOf('%>');

    // Get the position of the most recent opening tag
    const lastOpenTagWithExpressionPos = Math.max(lastOpenEqualsPos, lastOpenHyphenPos);
    const mostRecentOpenTag = Math.max(lastOpenTagWithExpressionPos, lastOpenScriptletPos);

    // Check if we're inside an expression
    if (mostRecentOpenTag > lastClosePos) {
        const isExpression =
            lastOpenEqualsPos === mostRecentOpenTag ||
            lastOpenHyphenPos === mostRecentOpenTag;
        const isScriptlet =
            lastOpenScriptletPos === mostRecentOpenTag &&
            lastOpenScriptletPos !== lastOpenEqualsPos &&
            lastOpenScriptletPos !== lastOpenHyphenPos;

        // Find the next closing tag
        const nextClosePos = textAfterCursor.indexOf('%>');
        if (nextClosePos === -1) return { inside: false }; // No closing tag found

        return {
            inside: true,
            isExpression,
            isScriptlet,
            expressionStart: mostRecentOpenTag + (isExpression ? 3 : 2), // Skip <%= or <%
            expressionEnd: position + nextClosePos
        };
    }

    return { inside: false };
};

/**
 * Helper function to safely format property values for display
 */
export const formatPropertyForDisplay = (value, formatPropertyDetail) => {
    if (!formatPropertyDetail) {
        // Default implementation if not provided
        const valueType = typeof value;
        if (valueType === 'string') {
            return `string: "${value.length > 20 ? value.substring(0, 20) + '...' : value}"`;
        } else if (valueType === 'number') {
            return `number: ${value}`;
        } else if (valueType === 'boolean') {
            return `boolean: ${value}`;
        } else if (valueType === 'object' && value !== null) {
            if (Array.isArray(value)) {
                return `array[${value.length}]`;
            } else {
                const objKeys = Object.keys(value);
                return `object: {${objKeys.slice(0, 3).join(', ')}${objKeys.length > 3 ? ', ...' : ''}}`;
            }
        }
        return valueType;
    }
    return formatPropertyDetail(value);
};

/**
 * EJS tag suggestions for completion
 */
export const createEjsTagSuggestions = (monaco) => {
    return [
        {
            label: '<%=  %>',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: '%= ${1:expression} %>',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'EJS output expression with HTML escaping',
            detail: 'Output the result of a JavaScript expression',
            sortText: '001'
        },
        {
            label: '<%-  %>',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: '%- ${1:expression} %>',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'EJS output expression without HTML escaping',
            detail: 'Output the result of a JavaScript expression without escaping',
            sortText: '002'
        },
        {
            label: '<%  %>',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: '% ${1:scriptlet} %>',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'EJS scriptlet',
            detail: 'Execute JavaScript without output',
            sortText: '003'
        },
        {
            label: '<% if () { %>  <% } %>',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: '% if (${1:condition}) { %>\n\t${2:content}\n<% } %>',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'EJS if statement',
            detail: 'Conditional content with if statement',
            sortText: '004'
        },
        {
            label: '<% for () { %>  <% } %>',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: '% for (let ${1:i} = 0; ${1:i} < ${2:array}.length; ${1:i}++) { %>\n\t${3:content}\n<% } %>',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'EJS for loop',
            detail: 'Loop over content with for statement',
            sortText: '005'
        },
        {
            label: '<% forEach () { %>  <% } %>',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: '% ${1:array}.forEach(function(${2:item}) { %>\n\t${3:content}\n<% }); %>',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'EJS forEach loop',
            detail: 'Loop over content with forEach',
            sortText: '006'
        }
    ];
};

/**
 * Common HTML tags for suggestions
 */
export const htmlTags = [
    { tag: 'div', detail: 'Block container' },
    { tag: 'span', detail: 'Inline container' },
    { tag: 'p', detail: 'Paragraph' },
    { tag: 'h1', detail: 'Heading level 1' },
    { tag: 'h2', detail: 'Heading level 2' },
    { tag: 'h3', detail: 'Heading level 3' },
    { tag: 'a', detail: 'Hyperlink', snippet: 'a href="${1:#}">${2:Link text}</a>' },
    { tag: 'table', detail: 'Table' },
    { tag: 'tr', detail: 'Table row' },
    { tag: 'td', detail: 'Table cell' },
    { tag: 'ul', detail: 'Unordered list' },
    { tag: 'ol', detail: 'Ordered list' },
    { tag: 'li', detail: 'List item' },
    { tag: 'br', detail: 'Line break', snippet: 'br />' },
    { tag: 'img', detail: 'Image', snippet: 'img src="${1}" alt="${2}" />' },
    { tag: 'input', detail: 'Input field', snippet: 'input type="${1:text}" name="${2:name}" />' },
    { tag: 'form', detail: 'Form container' },
    { tag: 'button', detail: 'Button element' },
    { tag: 'strong', detail: 'Strong emphasis' },
    { tag: 'em', detail: 'Emphasis' }
];

/**
 * JavaScript keywords for suggestions
 */
export const jsKeywords = [
    'break', 'case', 'catch', 'class', 'const', 'continue',
    'debugger', 'default', 'delete', 'do', 'else', 'export',
    'extends', 'false', 'finally', 'for', 'function', 'if',
    'import', 'in', 'instanceof', 'new', 'null', 'return',
    'super', 'switch', 'this', 'throw', 'true', 'try',
    'typeof', 'var', 'void', 'while', 'with', 'yield'
];
